/**
 * Site-wide reveal-on-scroll (reproduction of the old Concept theme's
 * "fade-up-large" effect, measured live).
 *
 * - fade-up: opacity 0 / translateY(44px, 24px on mobile) -> 1 / 0,
 *   900ms cubic-bezier(0.16, 1, 0.3, 1).
 * - zoom-out: slideshow/banner media scale(1.08) -> scale(1), same curve.
 * - Section headings are split into words with a 30ms stagger per word.
 * - Cards in product grids stagger by 60ms, capped at ~400ms total.
 * - Fail-safe: the hidden starting state exists only under
 *   html.reveal-init, which this script adds. No JS / JS error = fully
 *   visible page.
 * - prefers-reduced-motion: nothing is hidden or transformed.
 * - Compose with testimonial-parallax.js: cards carrying
 *   data-parallax-start get an opacity-only reveal so this script never
 *   writes transform on an element the parallax loop owns.
 */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reducedMotion) return;
  if (!('IntersectionObserver' in window)) return;

  var FADE_SELECTOR = [
    '.shopify-section .rich-text__blocks',
    '.shopify-section .contact',
    '.shopify-section .multicolumn-card',
    '.testimonials-stagger__card'
  ].join(',');

  var GRID_ITEM_SELECTOR = [
    '.shopify-section [id^="shopify-section-template"][data-section-type*="collection"] .card-wrapper',
    '.featured-collection .card-wrapper',
    '.featured-collection .grid__item',
    '.collection .card-wrapper',
    '.collection .grid__item'
  ].join(',');

  var HEADING_SELECTOR = '.shopify-section h2, .shopify-section .title';
  var ZOOM_SELECTOR = '.slideshow-component img, slideshow-component img, .banner__media img, .banner__media media-gallery img';

  var CARD_STAGGER_MS = 60;
  var CARD_STAGGER_CAP_MS = 400;
  var WORD_STAGGER_MS = 30;

  var docEl = document.documentElement;

  /** Elements we will animate, in DOM order, each with a delay (ms). */
  var pending = [];

  function isParallaxCard(el) {
    return el.hasAttribute('data-parallax-start') || !!el.closest('[data-parallax-start]');
  }

  function queue(el, delay, variant) {
    if (el.hasAttribute('data-reveal-queued')) return;
    // Never nest reveals: if an ancestor is already queued, animating both
    // would double the displacement (e.g. .card-wrapper inside .grid__item).
    if (el.parentElement && el.closest('[data-reveal-queued]')) return;
    el.setAttribute('data-reveal-queued', '');
    el.classList.add(variant === 'zoom' ? 'reveal-zoom' : 'reveal-fade');
    if (variant === 'opacity') el.classList.add('reveal-opacity-only');
    el.style.setProperty('--reveal-delay', delay + 'ms');
    pending.push(el);
  }

  /* Split a heading into word spans (whitespace-split, text preserved). */
  function splitHeading(heading) {
    if (heading.hasAttribute('data-reveal-split')) return;
    heading.setAttribute('data-reveal-split', '');
    var wordIndex = 0;
    var walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT, null);
    var textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);
    textNodes.forEach(function (node) {
      var text = node.nodeValue;
      var frag = document.createDocumentFragment();
      // Split on whitespace, keep the whitespace as plain text nodes so the
      // original text (incl. Greek) and wrapping behaviour are preserved.
      text.split(/(\s+)/).forEach(function (part) {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(part));
          return;
        }
        var span = document.createElement('span');
        span.className = 'reveal-word';
        span.textContent = part;
        span.style.setProperty('--reveal-delay', wordIndex * WORD_STAGGER_MS + 'ms');
        wordIndex += 1;
        frag.appendChild(span);
        pending.push(span);
      });
      node.parentNode.replaceChild(frag, node);
    });
  }

  /* Collect targets. */
  document.querySelectorAll(HEADING_SELECTOR).forEach(function (h) {
    // Skip headings that live inside an already-animated block to avoid nesting.
    if (h.closest('.rich-text__blocks, .multicolumn-card, .card-wrapper, .testimonials-stagger__card')) return;
    splitHeading(h);
  });

  var gridGroups = new Map();
  document.querySelectorAll(GRID_ITEM_SELECTOR).forEach(function (item) {
    if (item.closest('.testimonials-stagger')) return;
    var grid = item.closest('.grid, .product-grid, ul, ol') || item.parentElement;
    if (!gridGroups.has(grid)) gridGroups.set(grid, []);
    gridGroups.get(grid).push(item);
  });
  gridGroups.forEach(function (items) {
    items.forEach(function (item, i) {
      queue(item, Math.min(i * CARD_STAGGER_MS, CARD_STAGGER_CAP_MS), 'fade');
    });
  });

  document.querySelectorAll(FADE_SELECTOR).forEach(function (el) {
    if (isParallaxCard(el)) {
      // testimonial-parallax.js owns this element's transform — opacity only.
      queue(el, 0, 'opacity');
    } else {
      queue(el, 0, 'fade');
    }
  });

  document.querySelectorAll(ZOOM_SELECTOR).forEach(function (img) {
    queue(img, 0, 'zoom');
  });

  if (!pending.length) return;

  /* From here on the hidden state is safe: add the scope class. */
  docEl.classList.add('reveal-init');

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var el = entry.target;
      io.unobserve(el);
      el.classList.add('is-revealing');
      el.addEventListener('transitionend', function done(ev) {
        if (ev.propertyName !== 'opacity' && ev.propertyName !== 'transform') return;
        el.removeEventListener('transitionend', done);
        el.classList.add('is-revealed');
        el.classList.remove('is-revealing');
      });
    });
  }, { rootMargin: '0px 0px -10% 0px' });

  pending.forEach(function (el) { io.observe(el); });
})();
